
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { SignOutButton } from "@/components/SignOutButton";
import { ChangePasswordForm } from "@/components/auth/ChangePasswordForm";

export default function ProfileSettings() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: "",
    email: "",
  });

  useEffect(() => {
    if (!session) return;
    
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();

        if (error) throw error;
        
        if (data) {
          setProfileData({
            full_name: data.full_name || "",
            email: data.email || session.user.email || "",
          });
        }
      } catch (error: any) {
        console.error("Error fetching profile:", error.message);
        toast({
          title: "Error fetching profile",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [session, toast]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;

    try {
      setLoading(true);
      
      // Update profile in the database
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profileData.full_name,
        })
        .eq("id", session.user.id);

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error updating profile:", error.message);
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map(part => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  if (!session) {
    return <div>Please log in to view your profile settings.</div>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container py-8 mt-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Profile Settings</h1>
          
          {/* Profile Information Section */}
          <div className="bg-white rounded-lg border p-6 shadow-sm mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="h-16 w-16">
                <AvatarFallback className="text-lg bg-primary text-primary-foreground">
                  {getInitials(profileData.full_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-medium">{profileData.full_name || "Your Name"}</h2>
                <p className="text-muted-foreground">{profileData.email}</p>
              </div>
            </div>
            
            <Separator className="my-6" />
            
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input 
                  id="full_name"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                  placeholder="Your full name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email"
                  value={profileData.email}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>

                <SignOutButton />
              </div>
            </form>
          </div>

          {/* Password Management Section */}
          <div className="bg-white rounded-lg border p-6 shadow-sm mb-8">
            <h2 className="text-xl font-medium mb-4">Password Management</h2>
            <Separator className="mb-6" />
            
            <ChangePasswordForm />
          </div>
        </div>
      </main>
    </div>
  );
}
